<template>
	<div v-if="msgInfo.type != $enums.MESSAGE_TYPE.RECALL" class="chat-message-item" :class="active ? 'active' : ''">
		<div class="message-tip message-tip--system" v-if="msgInfo.type == $enums.MESSAGE_TYPE.TIP_TEXT"
			@click="onClickTipMessage($event)">
			<span class="message-tip__inner" v-html="parsedTipContent"></span>
		</div>
		<div class="message-tip message-tip--time" v-else-if="msgInfo.type == $enums.MESSAGE_TYPE.TIP_TIME">
			<span class="message-tip__inner">{{ $date.toTimeText(msgInfo.sendTime) }}</span>
		</div>
		<div class="message-normal " v-else-if="isNormal || isAction" :class="{ 'message-mine': mine }">
			<div class="avatar" @contextmenu.prevent.stop="showAvatarMenu($event)">
				<head-image :name="showName" :size="40" :url="headImage" :id="msgInfo.sendId"
					:isShowUserInfo="canViewMemberProfile"></head-image>
			</div>
			<div class="content">
				<div v-if="msgInfo.groupId && !msgInfo.selfSend" class="top">
					<div class="show-name">{{ showName }}</div>
					<el-tag v-if="isGroupOwner(msgInfo.sendId)" size="mini" type="danger">群主</el-tag>
					<el-tag v-if="isGroupManager(msgInfo.sendId)" size="mini" type="primary">管理员</el-tag>
				</div>
				<div class="bottom" @contextmenu.prevent="showMessageMenu($event)">
					<div class="message-main-row">
							<div ref="chatMsgBox" class="message-content-wrapper">
								<span v-if="isTopMessage" class="message-pin-indicator" title="该消息已置顶" aria-label="已置顶">
									<i class="el-icon-paperclip"></i>
								</span>
							<div class="message-text" v-if="isTextMessage && !isEditing" v-html="htmlText"
								@click="onClickTextMessage($event)">
							</div>
							<div class="message-edit" v-else-if="isTextMessage && isEditing">
								<el-input
									type="textarea"
									:autosize="{ minRows: 2, maxRows: 6 }"
									v-model="editContent"
									placeholder="编辑消息..."
									@keydown.enter.ctrl="onConfirmEdit"
									@keydown.esc="onCancelEdit">
								</el-input>
								<div class="edit-actions">
									<el-button size="mini" @click="onCancelEdit">取消</el-button>
									<el-button size="mini" type="primary" @click="onConfirmEdit">确定</el-button>
								</div>
							</div>
							<div
								class="message-image"
								:class="{ 'message-image--interactive': canPreviewImage }"
								v-else-if="msgInfo.type == $enums.MESSAGE_TYPE.IMAGE"
								@click.stop="showFullImage()"
							>
							<div class="image-container" :style="imageContainerStyle">
								<div
									v-if="showImagePlaceholder"
									class="image-placeholder"
									:class="{ 'image-placeholder--error': imageLoadError }"
								>
									<div v-if="!imageLoadError" class="image-placeholder__shimmer"></div>
									<div class="image-placeholder__content">
										<i :class="imagePlaceholderIcon"></i>
										<span>{{ imagePlaceholderText }}</span>
									</div>
								</div>
								<asset-image
									class="send-image"
									:class="{ 'send-image--ready': !showImagePlaceholder }"
									:style="imageStyle"
									:src="imageDisplaySource"
									:strict="true"
									:keep-previous="true"
									loading="lazy"
									decoding="async"
									@load="onImageLoad"
									@error="onImageResolveError"
									@resolve-error="onImageResolveError"
								/>
								<div v-if="attachmentTransferState" class="attachment-transfer attachment-transfer--overlay">
									<div class="attachment-transfer__body">
										<span class="attachment-transfer__label">{{ attachmentTransferState.text }}</span>
									</div>
									<div v-if="attachmentTransferState.progress" class="attachment-transfer__bar">
										<span></span>
									</div>
								</div>
								<div v-else-if="canPreviewImage" class="image-overlay">
									<i class="el-icon-zoom-in"></i>
								</div>
							</div>
						</div>
						<inline-video-player
							v-else-if="msgInfo.type == $enums.MESSAGE_TYPE.VIDEO"
							variant="message"
							:source="videoSource"
							:poster="videoPoster"
							:loading="videoPreparing"
							:load-error="videoLoadError"
							:disabled="assetUploadActive || assetUploadFailed"
							:previewable="canPreviewVideo"
							:duration="displayContentData.duration"
							:content-width="displayContentData.width"
							:content-height="displayContentData.height"
							:max-width="360"
							:max-height="360"
							:min-width="180"
							:fallback-aspect-ratio="videoAspectRatio"
							:transfer-state="attachmentTransferState"
							@request-load="onPlayVideo"
							@request-preview="onPreviewVideo"
							@error="onVideoLoadError"
							@poster-error="onVideoPosterError"
						/>
						<div class="message-file" v-else-if="msgInfo.type == $enums.MESSAGE_TYPE.FILE">
							<div
								class="file-box"
								@click.stop="onPreviewFile"
								:title="contentData.name"
								:class="{
									'file-box--interactive': canPreviewFile,
									'file-box--uploading': assetUploadActive,
									'file-box--failed': assetUploadFailed
								}"
							>
								<div class="file-main">
									<div class="file-info">
										<div class="file-name" @click.stop="onPreviewFile">{{
											contentData.name
										}}</div>
										<div class="file-meta">
											<div class="file-type-tag" :class="`file-type-tag--${fileTypeMeta.tone}`">{{ fileTypeMeta.fullLabel }}</div>
											<div class="file-size">{{ fileSize }}</div>
										</div>
									</div>
									<div class="file-icon" :class="`file-icon--${fileTypeMeta.tone}`" :title="`${fileTypeMeta.fullLabel} 文件`">
										<span class="file-icon__label">{{ fileTypeMeta.shortLabel }}</span>
									</div>
								</div>
								<div v-if="attachmentTransferState" class="attachment-transfer attachment-transfer--inline">
									<div class="attachment-transfer__body">
										<span class="attachment-transfer__label">{{ attachmentTransferState.text }}</span>
									</div>
									<div v-if="attachmentTransferState.progress" class="attachment-transfer__bar">
										<span></span>
									</div>
								</div>
							</div>
						</div>
						<div class="message-sticker" v-else-if="msgInfo.type == $enums.MESSAGE_TYPE.STICKER"
							@click="showFullSticker()">
							<asset-image :style="stickerStyle" :src="contentData.imageUrl" loading="lazy" />
						</div>
						<div class="message-voice" v-else-if="msgInfo.type == $enums.MESSAGE_TYPE.AUDIO">
							<button
								type="button"
								class="voice-chip"
								:class="{
									'voice-chip--playing': audioPlaying,
									'voice-chip--loading': audioPreparing
								}"
								:title="audioControlTitle"
								@click.stop="onPlayVoice"
							>
								<span class="voice-chip__icon">
									<i :class="audioControlIcon"></i>
								</span>
								<span class="voice-chip__wave" aria-hidden="true">
									<span class="voice-chip__bar"></span>
									<span class="voice-chip__bar"></span>
									<span class="voice-chip__bar"></span>
									<span class="voice-chip__bar"></span>
								</span>
								<span class="voice-chip__duration">{{ audioDurationText }}</span>
							</button>
						</div>
						<chat-user-card v-else-if="msgInfo.type == $enums.MESSAGE_TYPE.USER_CARD"
							:cardInfo="contentData"></chat-user-card>
						<chat-group-card v-else-if="msgInfo.type == $enums.MESSAGE_TYPE.GROUP_CARD"
							:cardInfo="contentData" :sendTime="msgInfo.sendTime"></chat-group-card>
						<div class="message-merge-forward" v-else-if="isMergeForward" @click="onClickMergeForward">
							<div class="merge-forward-header">
								<span class="merge-forward-title">{{ contentData.title }}</span>
								<span class="merge-forward-count">{{ contentData.messages.length }}条消息</span>
							</div>
							<div class="merge-forward-preview">
								<div class="preview-item" v-for="msg in mergeForwardPreview" :key="msg.renderKey">
									<span class="preview-sender">{{ msg.sendNickName }}:</span>
									<span class="preview-content" v-if="msg.type == $enums.MESSAGE_TYPE.TEXT"
										v-html="$emo.transform($str.html2Escape(msg.content), 'emoji-small')"></span>
									<span class="preview-content" v-else>{{
										$msgUtil.previewContent(msg) }}</span>
								</div>
							</div>
						</div>
						<div class="chat-action message-text" v-else-if="isAction">
							<span v-if="msgInfo.type == $enums.MESSAGE_TYPE.ACT_RT_VOICE" title="重新呼叫"
								@click="$emit('call')" class="iconfont icon-chat-voice"></span>
							<span v-if="msgInfo.type == $enums.MESSAGE_TYPE.ACT_RT_VIDEO" title="重新呼叫"
								@click="$emit('call')" class="iconfont icon-chat-video"></span>
							<span>{{ msgInfo.content }}</span>
						</div>
						<div v-else class="message-text">[暂不支持该消息类型]</div>
					</div>
					</div>
					<div class="quote-message" v-if="msgInfo.quoteMessage"
						@contextmenu.prevent.stop="showQuoteMenu($event)">
						<chat-quote-message :msgInfo="msgInfo.quoteMessage"
							@click.native.stop="$emit('locateQuote', msgInfo)"
							:showName="quoteShowName"></chat-quote-message>
					</div>
					<div class="message-send-state" v-if="sending">
						<span class="message-send-state-icon message-send-state-icon--sending" title="发送中">
							<i class="el-icon-loading"></i>
						</span>
					</div>
					<div class="message-send-state" v-else-if="sendFail">
						<button
							type="button"
							class="message-send-state-icon message-send-state-icon--failed"
							:title="sendFailTitle"
							@click="onSendFail"
						>
							<i class="el-icon-warning"></i>
						</button>
					</div>
					<div class="message-status" v-if="showPrivateReadStatus">
						<span
							class="message-status-icon"
							:class="`message-status-icon--${privateDeliveryIndicator.tone}`"
							:title="privateDeliveryIndicator.text"
						>
							<i :class="privateDeliveryIndicator.icon"></i>
							<span class="message-status-text">{{ privateDeliveryIndicator.text }}</span>
						</span>
					</div>
					<div class="chat-receipt" v-if="msgInfo.receipt" @click="onShowReadedBox">
						<span
							class="chat-receipt__pie"
							:class="`chat-receipt__pie--${groupReceiptIndicator.tone}`"
							:style="groupReceiptPieStyle"
							:title="groupReceiptIndicator.title"
						></span>
						<span>{{ groupReceiptIndicator.text }}</span>
					</div>
				</div>
			</div>
		</div>
		<right-menu ref="rightMenu" @select="onSelectMenu"></right-menu>
		<chat-group-readed ref="chatGroupReadedBox" :msgInfo="msgInfo" :group="group"
			:groupMembers="groupMembers"></chat-group-readed>
		<chat-merge-forward ref="mergeForward"></chat-merge-forward>
	</div>
</template>

<script>
import HeadImage from "../common/HeadImage.vue";
import AssetImage from "../common/AssetImage.vue";
import RightMenu from '../common/RightMenu.vue';
import ChatGroupReaded from './ChatGroupReaded.vue';
import ChatQuoteMessage from "./ChatQuoteMessage.vue";
import ChatUserCard from "./ChatUserCard.vue";
import ChatGroupCard from "./ChatGroupCard.vue";
import ChatMergeForward from "./ChatMergeForward.vue";
import InlineVideoPlayer from "./InlineVideoPlayer.vue";
import { getFileExtension, resolveFileTypeMeta } from "../../utils/fileTypeMeta";
export default {
	name: "messageItem",
	components: {
		HeadImage,
		AssetImage,
		RightMenu,
		ChatGroupReaded,
		ChatQuoteMessage,
		ChatUserCard,
		ChatGroupCard,
		ChatMergeForward,
		InlineVideoPlayer
	},
	props: {
		mode: {
			type: Number,
			default: 1
		},
		active: {
			type: Boolean,
			default: false
		},
		mine: {
			type: Boolean,
			required: true
		},
		headImage: {
			type: String,
			default: ''
		},
		showName: {
			type: String,
			required: true
		},
		quoteShowName: {
			type: String,
			default: ''
		},
		msgInfo: {
			type: Object,
			required: true
		},
		group: {
			type: Object,
		},
		groupMemberMap: {
			type: Object,
			default: () => ({})
		},
		groupMembers: {
			type: Array,
			default: () => []
		}
	},
	data() {
		return {
			isEditing: false,
			editContent: '',
			avatarMenuTarget: null,
			resolvedVideoUrl: '',
			resolvedVideoPoster: '',
			resolvedAudioUrl: '',
			videoPreparing: false,
			videoLoadError: false,
			audioPreparing: false,
			audioPlaying: false,
			mediaRequestId: 0,
			imageLoaded: false,
			imageLoadError: false,
			imageSource: '',
			imagePreviewUnlocked: false
		}
	},
	methods: {
		parseContent(content) {
			if (!content || typeof content !== 'string') {
				return {};
			}
			try {
				return JSON.parse(content);
			} catch (error) {
				return {};
			}
		},
		showAssetError(message) {
			this.$message.error(message || '资源暂不可用');
		},
		hasAssetSource(source) {
			return this.$assetResolver.hasAssetSource(source);
		},
		isLocalPreviewSource(source) {
			return /^(blob:|data:)/i.test(String(source || '').trim());
		},
		findGroupMember(userId) {
			if (userId == null) {
				return null;
			}
			return (this.groupMemberMap && this.groupMemberMap[String(userId)]) || null;
		},
		async syncMediaAssets() {
			const requestId = ++this.mediaRequestId;
			this.resetAudioState();
			this.resolvedVideoUrl = '';
			this.resolvedVideoPoster = '';
			this.resolvedAudioUrl = '';
			this.videoPreparing = false;
			this.videoLoadError = false;
			const nextImageSource = this.msgInfo.type == this.$enums.MESSAGE_TYPE.IMAGE ? this.imageDisplaySource : '';
			const keepImageState = Boolean(nextImageSource && nextImageSource === this.imageSource);
			// 源变化时保留已加载状态，新源解析完成前继续显示旧图，避免闪烁；
			// 解析失败由 onImageResolveError 恢复占位状态。
			if (!keepImageState) {
				this.imageLoadError = false;
				if (!nextImageSource) {
					this.imageLoaded = false;
				}
			}
			this.imageSource = nextImageSource;
			if (this.msgInfo.type != this.$enums.MESSAGE_TYPE.IMAGE) {
				this.imagePreviewUnlocked = false;
			}
			if (this.msgInfo.type == this.$enums.MESSAGE_TYPE.VIDEO) {
				this.resolvedVideoPoster = await this.resolveMediaUrl(this.displayContentData.coverUrl, requestId, {
					kind: 'cover'
				});
			}
		},
		async resolveMediaUrl(source, requestId, extra = {}, notifyError = false) {
			if (!source) {
				return '';
			}
			if (!this.hasAssetSource(source)) {
				if (notifyError && requestId === this.mediaRequestId) {
					this.showAssetError('资源暂不可用');
				}
				return '';
			}
			try {
				const url = await this.$assetResolver.resolveUrl(source, extra);
				if (requestId !== this.mediaRequestId) {
					return '';
				}
				return url;
			} catch (error) {
				if (notifyError && requestId === this.mediaRequestId) {
					this.showAssetError(error && error.message ? error.message : '资源加载失败');
				}
				return '';
			}
		},
		async onPlayVideo(event) {
			if (this.assetUploadActive || this.assetUploadFailed) {
				return;
			}
			if (this.resolvedVideoUrl || this.videoPreparing) {
				return;
			}
			if (event && typeof event.preventDefault === 'function') {
				event.preventDefault();
			}
			const requestId = this.mediaRequestId;
			this.videoPreparing = true;
			this.videoLoadError = false;
			const videoUrl = await this.resolveMediaUrl(this.displayContentData.videoUrl, requestId, {
				kind: 'video'
			}, true);
			if (!videoUrl || requestId !== this.mediaRequestId) {
				if (requestId === this.mediaRequestId) {
					this.videoPreparing = false;
					this.videoLoadError = true;
				}
				return;
			}
			this.resolvedVideoUrl = videoUrl;
			if (!this.resolvedVideoPoster) {
				this.resolvedVideoPoster = await this.resolveMediaUrl(this.displayContentData.coverUrl, requestId, {
					kind: 'cover'
				});
			}
			if (requestId === this.mediaRequestId) {
				this.videoPreparing = false;
				this.videoLoadError = false;
			}
		},
		onVideoLoadError() {
			if (!this.resolvedVideoUrl) {
				return;
			}
			this.resolvedVideoUrl = '';
			this.videoPreparing = false;
			this.videoLoadError = true;
			this.showAssetError('资源加载失败，请重试');
		},
		onVideoPosterError() {
			this.resolvedVideoPoster = '';
		},
		onImageLoad() {
			this.imageSource = this.imageDisplaySource;
			this.imageLoaded = true;
			this.imageLoadError = false;
			this.imagePreviewUnlocked = true;
		},
		onImageResolveError() {
			this.imageLoaded = false;
			this.imageLoadError = true;
		},
		openStrictImage(source, extra = {}) {
			if (!source) {
				this.showAssetError('图片暂不可用');
				return;
			}
			this.$eventBus.$emit("openFullImage", {
				url: source,
				strict: true,
				kind: 'image',
				...extra
			});
		},
		onSendFail() {
			this.$emit("resend", this.msgInfo);
		},
		parseTipMessage(content) {
			// 匹配格式：#{displayName:userId},正则表达式：#\{([^:]+):(\d+)\}
			const userMarkPattern = /#\{([^:]+):(\d+)\}/g;
			const maxInviteVisibleMembers = 8;
			const isInviteTip = typeof content === 'string' && content.indexOf('邀请') >= 0 && content.indexOf('加入了群聊') >= 0;
			let lastIndex = 0;
			let result = '';
			let match;
			let userMarkCount = 0;
			let hiddenUserCount = 0;
			while ((match = userMarkPattern.exec(content)) !== null) {
				const textBefore = content.substring(lastIndex, match.index);
				const shouldCollapseInviteMember = isInviteTip && userMarkCount > 0 && userMarkCount >= maxInviteVisibleMembers;
				if (shouldCollapseInviteMember) {
					hiddenUserCount += 1;
					lastIndex = match.index + match[0].length;
					userMarkCount += 1;
					continue;
				}
				// 添加匹配前的文本
				result += this.$str.html2Escape(textBefore);
				let displayName = match[1];
				const userId = match[2];
				// 如果是当前登录用户，用"你"代替用户昵称
				if(userId == this.userStore.userInfo.id){
					displayName = "你";
				}
				if (this.canViewMemberProfile) {
					result += `<span class="tip-user-name" data-user-id="${userId}">${this.$str.html2Escape(displayName)}</span>`;
				} else {
					result += this.$str.html2Escape(displayName);
				}
				lastIndex = match.index + match[0].length;
				userMarkCount += 1;
			}
			// 添加剩余文本
			result += this.$str.html2Escape(content.substring(lastIndex));
			const hasServerInviteSummary = isInviteTip && /等\d+人加入了群聊\s*$/.test(content);
			if (hiddenUserCount > 0 && !hasServerInviteSummary) {
				const suffixText = `等${hiddenUserCount}名成员`;
				result = result.replace(/(加入了群聊)\s*$/, `${suffixText}$1`);
			}
			return result;
		},
		onClickTipMessage(event) {
			if (!this.canViewMemberProfile) {
				return;
			}
			// 检查点击的是否是用户名元素
			const usernameEl = event.target.closest('.tip-user-name');
			if (usernameEl) {
				const userId = usernameEl.getAttribute('data-user-id');
				if (userId) {
					this.onClickTipUsername(event, parseInt(userId));
				}
			}
		},
		onClickTipUsername(event, userId) {
			if (!this.canViewMemberProfile) {
				return;
			}
			event.stopPropagation();
			// 获取用户信息
			this.$http({
				url: `/user/find/${userId}`,
				method: 'get'
			}).then(user => {
				let pos = {
					x: event.clientX + 30,
					y: event.clientY
				};
				this.$eventBus.$emit("openUserInfo", user, pos);
			})
		},
		onClickTextMessage(event) {
			// 文本消息中的 @用户名 高亮点击
			const atEl = event.target.closest('.chat-at-user');
			if (!atEl) return;
			const userId = atEl.getAttribute('data-user-id');
			// @全体成员(-1) 不弹用户信息
			if (!userId || userId === '-1') return;
			this.onClickTipUsername(event, parseInt(userId));
		},
		isMentionPrefixChar(char) {
			return !char || /[\s\u00A0\r\n\t"'“”‘’([{<《（【]/.test(char) || /[，。！？!?,、:：;；/\\|]/.test(char);
		},
		isMentionStopChar(char) {
			return !char || /[\s\u00A0\r\n\t"'“”‘’)\]}>,《》）】]/.test(char) || /[，。！？!?,、:：;；/\\|]/.test(char);
		},
		findNextMentionCandidate(text, startIndex = 0) {
			const source = String(text || '');
			for (let i = Math.max(0, startIndex); i < source.length; i++) {
				if (source[i] !== '@') {
					continue;
				}
				const prevChar = i > 0 ? source[i - 1] : '';
				if (!this.isMentionPrefixChar(prevChar)) {
					continue;
				}
				let end = i + 1;
				while (end < source.length && !this.isMentionStopChar(source[end])) {
					end++;
				}
				if (end <= i + 1) {
					continue;
				}
				return {
					start: i,
					end,
					text: source.slice(i, end)
				};
			}
			return null;
		},
		resolveMentionDisplayName(userId) {
			if (String(userId) === '-1') {
				return '全体成员';
			}
			const member = this.findGroupMember(userId);
			return member ? member.showNickName : '';
		},
		buildOrderedMentionEntries() {
			if (!Array.isArray(this.msgInfo.atUserIds)) {
				return [];
			}
			return this.msgInfo.atUserIds.map((userId) => {
				const displayName = this.resolveMentionDisplayName(userId);
				return {
					userId: String(userId),
					token: displayName ? `@${this.$str.html2Escape(displayName)}` : ''
				};
			});
		},
		findMentionMatch(text, cursor, entry) {
			const source = String(text || '');
			const searchFrom = Math.max(0, cursor);
			if (entry && entry.token) {
				let offset = searchFrom;
				while (offset < source.length) {
					const index = source.indexOf(entry.token, offset);
					if (index < 0) {
						break;
					}
					const prevChar = index > 0 ? source[index - 1] : '';
					const nextChar = source[index + entry.token.length] || '';
					if (this.isMentionPrefixChar(prevChar) && this.isMentionStopChar(nextChar)) {
						return {
							start: index,
							end: index + entry.token.length,
							text: entry.token
						};
					}
					offset = index + entry.token.length;
				}
			}
			return this.findNextMentionCandidate(source, searchFrom);
		},
		highlightAtUsers(escapedText) {
			// 仅群聊文本消息才有 atUserIds 语义
			if (!this.isGroupMessage
				|| !Array.isArray(this.msgInfo.atUserIds) || this.msgInfo.atUserIds.length === 0) {
				return escapedText;
			}
			const source = String(escapedText || '');
			const entries = this.buildOrderedMentionEntries();
			if (!entries.length) {
				return source;
			}
			let cursor = 0;
			let result = '';
			entries.forEach((entry) => {
				const match = this.findMentionMatch(source, cursor, entry);
				if (!match || match.start < cursor) {
					return;
				}
				result += source.slice(cursor, match.start);
				result += `<span class="chat-at-user" data-user-id="${entry.userId}">${match.text}</span>`;
				cursor = match.end;
			});
			result += source.slice(cursor);
			return result;
		},
		showFullImage() {
			if (!this.canPreviewImage) {
				return;
			}
			let imageUrl = this.imagePreviewSource;
			if (imageUrl) {
				this.openStrictImage(imageUrl, this.imagePreviewMeta);
			}
		},
		showFullSticker() {
			let imageUrl = this.contentData.imageUrl;
			if (imageUrl) {
				this.$eventBus.$emit("openFullImage", imageUrl);
			}
		},
		onPreviewVideo() {
			if (this.assetUploadActive) {
				this.showAssetError('视频上传中，请稍后');
				return;
			}
			if (this.assetUploadFailed) {
				this.showAssetError('视频上传失败，请点击下方重试');
				return;
			}
			if (!this.displayContentData.videoUrl) {
				this.showAssetError('视频暂不可用');
				return;
			}
			this.$eventBus.$emit("openFullVideo", {
				url: this.displayContentData.videoUrl,
				poster: this.displayContentData.coverUrl || '',
				strict: true
			});
		},
		ensureAudioPlayer() {
			if (this.audio) {
				return;
			}
			const player = new Audio();
			player.preload = 'auto';
			player.onplay = () => {
				this.audioPreparing = false;
				this.audioPlaying = true;
			};
			player.onpause = () => {
				this.audioPreparing = false;
				this.audioPlaying = false;
			};
			player.onended = () => {
				this.audioPreparing = false;
				this.audioPlaying = false;
			};
			player.onerror = () => {
				this.onAudioLoadError();
			};
			this.audio = player;
		},
		resetAudioState() {
			this.audioPreparing = false;
			this.audioPlaying = false;
			if (!this.audio) {
				return;
			}
			this.audio.pause();
			this.audio.removeAttribute('src');
			if (typeof this.audio.load === 'function') {
				this.audio.load();
			}
		},
		async onPlayVoice(event) {
			if (event && typeof event.preventDefault === 'function') {
				event.preventDefault();
			}
			this.ensureAudioPlayer();
			if (this.audioPlaying) {
				this.audio.pause();
				return;
			}
			const requestId = this.mediaRequestId;
			this.audioPreparing = true;
			const audioUrl = this.resolvedAudioUrl || await this.resolveMediaUrl(this.contentData.url, requestId, {
				kind: 'audio'
			}, true);
			if (!audioUrl || requestId !== this.mediaRequestId) {
				if (requestId === this.mediaRequestId) {
					this.audioPreparing = false;
				}
				return;
			}
			this.resolvedAudioUrl = audioUrl;
			if (this.audio.src !== audioUrl) {
				this.audio.src = audioUrl;
				if (typeof this.audio.load === 'function') {
					this.audio.load();
				}
			}
			this.audio.play().catch(() => {
				this.audioPreparing = false;
				this.audioPlaying = false;
				this.showAssetError('资源加载失败，请重试');
			});
		},
		onAudioLoadError() {
			const hasResolvedAudio = Boolean(this.resolvedAudioUrl || (this.audio && this.audio.src));
			this.resolvedAudioUrl = '';
			this.resetAudioState();
			if (hasResolvedAudio) {
				this.showAssetError('资源加载失败，请重试');
			}
		},
		showMessageMenu(e) {
			let menuItems = [];
			if (this.isCopyableMessage) {
				menuItems.push({
					key: 'COPY',
					name: '复制',
					icon: 'el-icon-document-copy'
				});
			}
			// 表情消息可以添加到自定义表情
			if (this.msgInfo.type == this.$enums.MESSAGE_TYPE.STICKER && this.contentData.albumId) {
				menuItems.push({
					key: 'ADD_STICKER',
					name: '添加到表情',
					icon: 'el-icon-star-off'
				});
			}
			// 多选选项（需要权限）
			if ((this.isNormal || this.isAction) && this.$permission.hasRolePermission('message.multiSelect')) {
				menuItems.push({
					key: 'MULTI_SELECT',
					name: '多选',
					icon: 'el-icon-circle-check'
				});
			}
			menuItems.push({
				key: 'DELETE',
				name: '删除',
				icon: 'el-icon-delete',
				danger: true
			});
			if (this.msgInfo.id) {
				// 编辑选项（仅文本消息，仅自己的消息，需要权限）
				if (this.$permission.canEditMessage(this.msgInfo)) {
					menuItems.push({
						key: 'EDIT',
						name: '编辑',
						icon: 'el-icon-edit'
					});
				}
				// 撤回选项（考虑时间限制和角色）
				const groupId = this.msgInfo.groupId || null;
				if (this.$permission.canRecallMessage(this.msgInfo, groupId) ||
					(groupId && (this.isOwner || this.isManager))) {
					menuItems.push({
						key: 'RECALL',
						name: '撤回',
						icon: 'el-icon-refresh'
					});
				}
				if (this.isNormal) {
					menuItems.push({
						key: 'QUOTE',
						name: '引用',
						icon: 'el-icon-chat-dot-round'
					});
				}
				// 转发选项（需要权限）
				if (this.isNormal && this.canForwardMessage) {
					menuItems.push({
						key: 'FORWARD',
						name: '转发',
						icon: 'el-icon-s-promotion'
					});
				}
				// 群消息置顶（群主/管理员，检查配置）
				if ((this.isOwner || this.isManager) && this.isNormal && this.msgInfo.groupId) {
					const messageTopLimit = this.$permission.getGroupMessageTopLimit();
					if (messageTopLimit !== 0) {
						menuItems.push({
							key: 'TOP',
							name: this.isTopMessage ? '取消置顶' : '置顶',
							icon: 'el-icon-top'
						});
					}
				}
			}
			if (this.msgInfo.type == this.$enums.MESSAGE_TYPE.FILE) {
				menuItems.push({
					key: 'DOWNLOAD',
					name: '下载',
					icon: 'el-icon-download'
				});
			}
			this.$refs.rightMenu.open(e, menuItems);
		},
		canManageMessageSender(userId) {
			if (!this.isGroupMessage || !this.group || !this.group.id
				|| !this.isNormal || userId == null || userId == this.userStore.userInfo.id
				|| userId == this.group.ownerId) {
				return false;
			}
			// Read the live lookup: member refreshes can replace its non-reactive backing map.
			if (!this.groupStore.isManager(this.group.id, this.userStore.userInfo.id)) {
				return false;
			}
			const member = this.findGroupMember(userId);
			return !member || (!member.quit && !member.isManager);
		},
		isOrdinaryRemovalCandidate(member, userId) {
			return Boolean(member && String(member.userId) === String(userId)
				&& !member.quit && !member.isManager
				&& String(userId) !== String(this.group.ownerId)
				&& String(userId) !== String(this.userStore.userInfo.id));
		},
		async showAvatarMenu(event) {
			const groupId = this.msgInfo.groupId;
			const userId = this.msgInfo.sendId;
			this.avatarMenuTarget = null;
			if (!this.canManageMessageSender(userId)) {
				return;
			}
			let member = this.findGroupMember(userId);
			if (!member) {
				try {
					const page = await this.$http({
						url: `/group/${groupId}/member-candidates/remove?targetUserId=${encodeURIComponent(userId)}&limit=1`,
						method: 'get',
						_skipErrorMessage: true
					});
					member = page && Array.isArray(page.items) ? page.items[0] : null;
				} catch (error) {
					return;
				}
			}
			if (this.msgInfo.groupId != groupId || this.msgInfo.sendId != userId
				|| !this.canManageMessageSender(userId) || !this.isOrdinaryRemovalCandidate(member, userId)) {
				return;
			}
			this.avatarMenuTarget = member;
			this.$refs.rightMenu.open(event, [{
				key: 'KICK_MEMBER',
				name: '移出本群',
				icon: 'el-icon-delete',
				danger: true
			}]);
		},
		showQuoteMenu(e) {
			let menuItems = [];
			if (this.msgInfo.quoteMessage &&
				this.msgInfo.quoteMessage.status != this.$enums.MESSAGE_STATUS.RECALL) {
				menuItems.push({
					key: 'LOCATE_QUOTE',
					name: '定位到原消息',
					icon: 'el-icon-location'
				});
			}
			this.$refs.rightMenu.open(e, menuItems);
		},
		onSelectMenu(item) {
			if (item.key === 'KICK_MEMBER') {
				this.kickMessageSender();
				return;
			}
			// 编辑菜单特殊处理
			if (item.key === 'EDIT') {
				this.onStartEdit();
				return;
			}
			// 菜单id转驼峰作为事件key
			let eventKey = item.key.toLowerCase().replace(/_([a-z])/g, (g) => g[1].toUpperCase());
			this.$emit(eventKey, this.msgInfo);
		},
		kickMessageSender() {
			const member = this.avatarMenuTarget;
			if (!member || !this.canManageMessageSender(member.userId)
				|| !this.isOrdinaryRemovalCandidate(member, member.userId)) {
				return;
			}
			const name = member.showNickName || this.showName;
			this.$confirm(`确定将成员'${name}'移出群聊吗？`, '确认移出?', {
				confirmButtonText: '确定',
				cancelButtonText: '取消',
				type: 'warning'
			}).then(() => this.$http({
				url: '/group/members/remove',
				method: 'delete',
				data: {
					groupId: this.msgInfo.groupId,
					userIds: [member.userId]
				}
			})).then(() => {
				this.$message.success(`您将'${name}'移出了群聊`);
			}, error => {
				if (error !== 'cancel' && error !== 'close') {
					this.groupStore.refreshMember(this.msgInfo.groupId, { forceRefresh: true }).catch(() => []);
				}
			});
		},
		onShowReadedBox() {
			let rect = this.$refs.chatMsgBox.getBoundingClientRect();
			this.$refs.chatGroupReadedBox.open(rect);
		},
		isGroupOwner(userId) {
			return this.group.ownerId == userId;
		},
		isGroupManager(userId) {
			let m = this.findGroupMember(userId);
			return m && m.isManager
		},
		onClickMergeForward() {
			if (this.isMergeForward && this.contentData) {
				this.$refs.mergeForward.open(this.contentData, this.groupMembers);
			}
		},
		onPreviewFile() {
			if (this.assetUploadActive) {
				this.showAssetError('文件上传中，请稍后');
				return;
			}
			if (this.assetUploadFailed) {
				this.showAssetError('文件上传失败，请点击下方重试');
				return;
			}
			if (!this.contentData.url) {
				this.showAssetError('文件暂不可用');
				return;
			}
			this.$eventBus.$emit("openPreviewFile", {
				url: this.contentData.url,
				fileName: this.contentData.name,
				strict: true
			});
		},
		onStartEdit() {
			this.isEditing = true;
			this.editContent = this.msgInfo.content;
		},
		onCancelEdit() {
			this.isEditing = false;
			this.editContent = '';
		},
		onConfirmEdit() {
			if (!this.editContent || !this.editContent.trim()) {
				this.$message.warning('消息内容不能为空');
				return;
			}
			this.$emit('edit', {
				msgInfo: this.msgInfo,
				newContent: this.editContent.trim()
			});
			this.isEditing = false;
			this.editContent = '';
		},
		buildMediaStyle(maxSize) {
			return {
				display: 'block',
				width: 'auto',
				height: 'auto',
				maxWidth: `min(100%, ${maxSize}px)`,
				maxHeight: `${maxSize}px`
			};
		},
		buildFallbackImageContainerStyle(aspectRatio = '4 / 3') {
			return {
				width: '220px',
				maxWidth: '100%',
				aspectRatio
			};
		},
		getNormalizedImageAspectRatio(width, height) {
			if (!width || !height) {
				return '4 / 3';
			}
			const ratio = width / height;
			if (ratio >= 1) {
				return `${Math.min(Math.max(ratio, 1), 1.6)} / 1`;
			}
			return `1 / ${Math.min(Math.max(height / width, 1), 1.6)}`;
		},
		shouldUseStabilizedImageFrame(width, height) {
			if (!width || !height) {
				return true;
			}
			if (width < 96 || height < 96) {
				return true;
			}
			const ratio = width / height;
			return ratio > 3 || ratio < 1 / 3;
		},
		buildImageContainerStyle(maxSize) {
			const width = Number(this.displayContentData && this.displayContentData.width) || 0;
			const height = Number(this.displayContentData && this.displayContentData.height) || 0;
			if (this.showImagePlaceholder && this.shouldUseStabilizedImageFrame(width, height)) {
				return this.buildFallbackImageContainerStyle(
					this.getNormalizedImageAspectRatio(width, height)
				);
			}
			if (!width || !height) {
				return this.buildFallbackImageContainerStyle();
			}
			const scale = Math.min(1, maxSize / width, maxSize / height);
			return {
				width: `${Math.max(1, Math.round(width * scale))}px`,
				maxWidth: '100%',
				aspectRatio: `${Math.max(1, width)} / ${Math.max(1, height)}`
			};
		}
	},
	computed: {
		parsedTipContent() {
			// 保留 #{name:id} 标记，才能把可点击的人名渲染成 span
			return this.parseTipMessage(this.$msgUtil.displayTipContent(this.msgInfo.content));
		},
		sending() {
			return this.msgInfo.status == this.$enums.MESSAGE_STATUS.SENDING && !this.attachmentTransferState;
		},
		sendFail() {
			return this.assetUploadFailed || this.msgInfo.status == this.$enums.MESSAGE_STATUS.FAILED;
		},
		sendFailTitle() {
			return this.assetUploadFailed ? '上传失败，点击重试' : '发送失败，点击重试';
		},
		contentData() {
			return this.parseContent(this.msgInfo.content)
		},
		localPreviewData() {
			if (!this.msgInfo.selfSend || !this.msgInfo.localPreviewContent) {
				return null;
			}
			const previewData = this.parseContent(this.msgInfo.localPreviewContent);
			return Object.keys(previewData).length > 0 ? previewData : null;
		},
		displayContentData() {
			return this.localPreviewData ? {
				...this.contentData,
				...this.localPreviewData
			} : this.contentData;
		},
		videoSource() {
			if (this.localPreviewData && this.displayContentData.videoUrl) {
				return this.displayContentData.videoUrl;
			}
			return this.resolvedVideoUrl || undefined;
		},
		videoPoster() {
			if (this.isLocalPreviewSource(this.displayContentData.coverUrl)) {
				return this.displayContentData.coverUrl;
			}
			return this.resolvedVideoPoster || undefined;
		},
		videoAspectRatio() {
			const width = Number(this.displayContentData.width) || 0;
			const height = Number(this.displayContentData.height) || 0;
			if (!width || !height) {
				return '16 / 9';
			}
			return `${Math.max(1, width)} / ${Math.max(1, height)}`;
		},
		videoMaskIcon() {
			if (this.videoPreparing) {
				return 'el-icon-loading';
			}
			if (this.videoLoadError) {
				return 'el-icon-video-camera';
			}
			return 'el-icon-video-play';
		},
		videoMaskText() {
			if (this.videoPreparing) {
				return '视频加载中';
			}
			if (this.videoLoadError) {
				return '视频暂不可用';
			}
			if (!this.videoPoster) {
				return '点击播放视频';
			}
			return '';
		},
		showVideoMaskText() {
			return Boolean(this.videoMaskText);
		},
		audioSource() {
			return this.resolvedAudioUrl || undefined;
		},
		audioDurationText() {
			const duration = Number(this.contentData.duration);
			if (!Number.isFinite(duration) || duration <= 0) {
				return '语音';
			}
			return `${Math.max(1, Math.round(duration))}"`;
		},
		audioControlIcon() {
			if (this.audioPreparing) {
				return 'el-icon-loading';
			}
			return this.audioPlaying ? 'el-icon-video-pause' : 'el-icon-caret-right';
		},
		audioControlTitle() {
			if (this.audioPreparing) {
				return '语音加载中';
			}
			return this.audioPlaying ? '暂停播放' : '播放语音';
		},
		fileSize() {
			let size = this.contentData.size;
			if (size > 1024 * 1024) {
				return Math.round(size / 1024 / 1024) + "M";
			}
			if (size > 1024) {
				return Math.round(size / 1024) + "KB";
			}
			return size + "B";
		},
		fileTypeMeta() {
			return resolveFileTypeMeta(getFileExtension(this.contentData.name));
		},
		isTextMessage() {
			return this.msgInfo.type == this.$enums.MESSAGE_TYPE.TEXT
		},
		isAttachmentMessage() {
			return this.msgInfo.type == this.$enums.MESSAGE_TYPE.IMAGE
				|| this.msgInfo.type == this.$enums.MESSAGE_TYPE.VIDEO
				|| this.msgInfo.type == this.$enums.MESSAGE_TYPE.FILE;
		},
		assetUploadState() {
			return this.msgInfo.localAssetState || '';
		},
		assetUploadActive() {
			return this.isAttachmentMessage && this.assetUploadState === 'uploading';
		},
		assetUploadFailed() {
			return this.isAttachmentMessage && this.assetUploadState === 'failed';
		},
		attachmentTransferState() {
			if (!this.isAttachmentMessage) {
				return null;
			}
			if (this.assetUploadActive) {
				return {
					text: this.msgInfo.localAssetText || '附件上传中',
					progress: true
				};
			}
			if (this.assetUploadFailed) {
				return {
					text: this.msgInfo.localAssetText || '附件上传失败',
					progress: false
				};
			}
			return null;
		},
		showImagePlaceholder() {
			return this.msgInfo.type == this.$enums.MESSAGE_TYPE.IMAGE && !this.imageLoaded;
		},
		imagePlaceholderText() {
			return this.imageLoadError ? '图片暂不可用' : '图片加载中';
		},
		imagePlaceholderIcon() {
			return this.imageLoadError ? 'el-icon-picture-outline' : 'el-icon-loading';
		},
		imagePreviewSource() {
			return this.displayContentData.originUrl || this.displayContentData.thumbUrl || '';
		},
		imagePreviewMeta() {
			return {
				fileName: this.displayContentData.name || this.displayContentData.fileName || 'image',
				contentType: this.displayContentData.contentType || this.displayContentData.mimeType || 'image/*'
			};
		},
		imageDisplaySource() {
			return this.displayContentData.thumbUrl || this.displayContentData.originUrl || '';
		},
		canPreviewImage() {
			return this.msgInfo.type == this.$enums.MESSAGE_TYPE.IMAGE
				&& Boolean(this.imagePreviewSource)
				&& !this.assetUploadFailed;
		},
		canPreviewVideo() {
			return this.msgInfo.type == this.$enums.MESSAGE_TYPE.VIDEO
				&& Boolean(this.displayContentData.videoUrl)
				&& !this.attachmentTransferState;
		},
		canPreviewFile() {
			return this.msgInfo.type == this.$enums.MESSAGE_TYPE.FILE
				&& !this.attachmentTransferState
				&& Boolean(this.contentData.url);
		},
		isCopyableMessage() {
			if (this.isTextMessage) {
				return true;
			}
			if (this.msgInfo.type == this.$enums.MESSAGE_TYPE.IMAGE) {
				return Boolean(this.displayContentData.originUrl || this.displayContentData.thumbUrl);
			}
			if (this.msgInfo.type == this.$enums.MESSAGE_TYPE.VIDEO) {
				return Boolean(this.displayContentData.videoUrl);
			}
			return false;
		},
		isMergeForward() {
			return this.msgInfo.type == this.$enums.MESSAGE_TYPE.MERGE_FORWARD
		},
		isCardMessages() {
			return this.msgInfo.type == this.$enums.MESSAGE_TYPE.USER_CARD ||
				this.msgInfo.type == this.$enums.MESSAGE_TYPE.GROUP_CARD
		},
		canForwardMessage() {
			if (!this.$permission.hasRolePermission('message.forward')) {
				return false;
			}
			if (!this.isCardMessages) {
				return true;
			}
			return this.$permission.canForwardCardMessage
				? this.$permission.canForwardCardMessage()
				: this.$permission.hasRolePermission('message.cardForward');
		},
		mergeForwardPreview() {
			// 只显示前3条消息的预览
			return this.contentData.messages.slice(0, 3).map((message) => ({
				...message,
				renderKey: message.id || message.localId || message.tmpId ||
					`${message.sendId || 0}:${message.sendTime || 0}:${message.type || 0}:${message.content || ''}`
			}))
		},
		isAction() {
			return this.$msgType.isAction(this.msgInfo.type);
		},
		isNormal() {
			return this.$msgType.isNormal(this.msgInfo.type);
		},
		isOwner() {
			let userId = this.userStore.userInfo.id;
			return this.group && userId == this.group.ownerId
		},
		isManager() {
			if (!this.group || !this.group.id) {
				return false;
			}
			return this.groupStore.isManager(this.group.id, this.userStore.userInfo.id);
		},
		htmlText() {
			let color = this.msgInfo.selfSend ? 'white' : '';
			let text = this.$str.html2Escape(this.msgInfo.content)
			text = this.highlightAtUsers(text);
			text = this.$url.replaceURLWithHTMLLinks(text, color)
			return this.$emo.transform(text, 'emoji-normal')
		},
		isGroupMessage() {
			return !!this.msgInfo.groupId;
		},
		showPrivateReadStatus() {
			return !this.isAction && this.msgInfo.selfSend && !this.isGroupMessage
				&& !this.sending && !this.sendFail;
		},
		privateDeliveryIndicator() {
			if (this.msgInfo.status == this.$enums.MESSAGE_STATUS.READED) {
				return {
					tone: 'readed',
					text: '已读',
					icon: 'el-icon-check'
				};
			}
			return {
				tone: 'unread',
				text: '未读',
				icon: 'el-icon-check'
			};
		},
		groupReceiptIndicator() {
			const readedCount = Number(this.msgInfo.readedCount) || 0;
			const total = (this.groupMembers || []).filter(member => {
				return member && !member.quit && member.userId != this.msgInfo.sendId;
			}).length;
			const percent = this.msgInfo.receiptOk
				? 100
				: (total > 0 ? Math.min(100, Math.max(0, Math.round(readedCount * 100 / total))) : 0);
			const text = total > 0
				? `${readedCount}/${total}已读`
				: `${readedCount}人已读`;
			return {
				readedCount,
				total,
				percent,
				text: this.msgInfo.receiptOk ? '全体已读' : text,
				title: this.msgInfo.receiptOk ? '全体已读' : text,
				tone: this.msgInfo.receiptOk ? 'readed' : 'partial'
			};
		},
		groupReceiptPieStyle() {
			const degree = Math.max(0, Math.min(360, this.groupReceiptIndicator.percent * 3.6));
			return {
				background: `conic-gradient(var(--im-color-success) 0deg ${degree}deg, rgba(var(--im-black-rgb), 0.16) ${degree}deg 360deg)`
			};
		},
		canViewMemberProfile() {
			if (!this.msgInfo.groupId || !this.group || !this.group.id) {
				return true;
			}
			return this.$permission.hasGroupPermission(this.group.id, 'isAllowViewMemberProfile');
		},
		imageStyle() {
			return {
				display: 'block',
				width: '100%',
				height: 'auto',
				maxWidth: '100%'
			};
		},
		imageContainerStyle() {
			return this.buildImageContainerStyle(360);
		},
		isTopMessage() {
			if (!this.msgInfo.id || !this.group) return false;
			const topMessages = Array.isArray(this.group.topMessages) ? this.group.topMessages : [];
			return topMessages.some(m => m.id == this.msgInfo.id);
		},
		stickerStyle() {
			return this.buildMediaStyle(180);
		}
	},
	watch: {
		'msgInfo.content': {
			immediate: true,
			handler() {
				this.syncMediaAssets();
			}
		}
	},
	beforeDestroy() {
		this.mediaRequestId += 1;
		if (this.audio) {
			this.audio.pause();
			this.audio = null;
		}
	}
}
</script>

<style lang="scss" scoped>
.chat-message-item {
	padding: 4px 12px;
	border-radius: 12px;
	transition: background-color 0.2s ease;

	&.active {
		background: rgba(var(--im-primary-rgb), 0.08);
	}

	.message-tip {
		text-align: center;
		padding: 6px 0;
		line-height: 1.6;
		font-size: 12px;
		color: var(--im-text-color-light);
		word-break: break-word;

		.message-tip__inner {
			display: inline-block;
		}

		:deep(.tip-user-name) {
			color: var(--im-color-primary);
			cursor: pointer;
			padding: 2px 5px;
			font-weight: 600;
		}

		&.message-tip--system {
			.message-tip__inner {
				color: var(--im-text-color-secondary);
			}
		}

		&.message-tip--time {
			font-size: 11px;
			color: var(--im-text-color-lighter);

			.message-tip__inner {
				background: transparent;
				border: none;
				padding: 0 8px;
			}
		}
	}

	.message-normal {
		position: relative;
		font-size: 0;
		padding-left: 56px;
		min-height: 52px;
		margin: 8px 0;

		.avatar {
			position: absolute;
			width: 40px;
			height: 40px;
			top: 2px;
			left: 0;
		}

		.content {
			display: flex;
			flex-direction: column;
			align-items: flex-start;
			text-align: left;
			min-width: 0;
			max-width: 100%;

			.top {
				display: flex;
				flex-wrap: nowrap;
				align-items: center;
				min-width: 0;
				min-height: 20px;
				gap: 8px;

				.show-name {
					white-space: nowrap;
					max-width: 400px;
					overflow: hidden;
					line-height: 20px;
					font-size: 13px;
					font-weight: 500;
					color: var(--im-text-color-secondary);
				}
			}


				.bottom {
					display: inline-flex;
					flex-direction: column;
					align-items: flex-start;
					width: fit-content;
				max-width: 100%;
				min-width: 0;
				padding-right: 32px;
				margin-top: 4px;
				box-sizing: border-box;

					.message-main-row {
						display: inline-flex;
						align-items: flex-end;
						flex-wrap: nowrap;
						gap: 8px;
						width: auto;
						max-width: 100%;
						min-width: 0;
						align-self: flex-start;
					}

					.message-content-wrapper {
						position: relative;
						display: inline-flex;
						align-items: flex-end;
						width: fit-content;
						flex: 0 0 auto;
						max-width: 100%;
						min-width: 0;
						overflow: visible;
					}

					.message-pin-indicator {
						position: absolute;
						top: -4px;
						right: -4px;
						left: auto;
						z-index: 3;
						display: inline-flex;
						align-items: center;
						justify-content: center;
						width: 14px;
						height: 14px;
						border-radius: 999px;
						border: 1px solid rgba(var(--im-white-rgb), 0.72);
						background: rgba(var(--im-white-rgb), 0.8);
						box-shadow:
							0 4px 8px -8px rgba(var(--im-black-rgb), 0.2),
							0 1px 2px rgba(var(--im-black-rgb), 0.08);
						color: rgba(var(--im-orange-rgb), 0.82);
						opacity: 0.9;
						backdrop-filter: blur(6px);
						pointer-events: none;

						i {
							font-size: 8px;
							line-height: 1;
							transform: translateY(-0.5px);
						}
					}

					.message-text {
						display: inline-block;
						position: relative;
						max-width: min(100%, 680px);
						min-width: 0;
						line-height: 24px;
						padding: 8px 12px;
						background: var(--bubble-recv-bg);
						color: var(--bubble-recv-text);
						border: 1px solid rgba(var(--im-black-rgb), 0.05);
						border-radius: 8px 16px 16px 16px;
						font-size: 14px;
						text-align: left;
						white-space: pre-wrap;
						word-break: break-word;
						overflow-wrap: anywhere;
					}

				.message-edit {
					display: block;
					background: var(--im-control-bg);
					border-radius: 8px;
					border: 1px solid var(--im-control-border);
					padding: 10px;
					width: min(100%, 420px);
					max-width: 100%;
					min-width: 0;
					box-sizing: border-box;

					.edit-actions {
						display: flex;
						justify-content: flex-end;
						gap: 8px;
						margin-top: 8px;
					}
				}

				.message-merge-forward {
					display: block;
					background: var(--bubble-recv-bg);
					border-radius: 10px;
					padding: 12px;
					cursor: pointer;
					transition: background-color 0.2s ease, border-color 0.2s ease;
					border: 1px solid var(--im-control-border);
					width: min(100%, 400px);
					max-width: 100%;
					min-width: 0;
					box-sizing: border-box;

					&:hover {
						background: rgba(var(--im-primary-rgb), 0.05);
						border-color: rgba(var(--im-primary-rgb), 0.18);
					}

					.merge-forward-header {
						display: flex;
						align-items: center;
						gap: 8px;
						margin-bottom: 8px;
						padding-bottom: 8px;
						border-bottom: 1px solid var(--im-border-color);
						text-align: left;

						.merge-forward-title {
							font-size: var(--im-font-size);
							font-weight: 600;
							color: var(--im-text-color);
							word-break: break-all
						}

						.merge-forward-count {
							font-size: var(--im-font-size-small);
							color: var(--im-text-color-light);
							margin-left: auto;
							white-space: nowrap;
						}
					}

					.merge-forward-preview {

						.preview-item {
							font-size: var(--im-font-size-small);
							margin-bottom: 4px;
							overflow: hidden;
							text-align: left;

							.preview-sender {
								color: var(--im-text-color);
							}

							.preview-content {
								margin-left: 4px;
								color: var(--im-text-color-light);
								word-break: break-all;
							}
						}
					}
				}

				.attachment-transfer {
					display: flex;
					flex-direction: column;
					gap: 8px;

					.attachment-transfer__body {
						display: flex;
						align-items: center;
						justify-content: space-between;
						gap: 10px;
					}

					.attachment-transfer__label {
						font-size: 12px;
						line-height: 1.2;
						font-weight: 500;
					}

					.attachment-transfer__bar {
						position: relative;
						height: 3px;
						border-radius: 999px;
						background: rgba(var(--im-white-rgb), 0.24);
						overflow: hidden;

						span {
							position: absolute;
							top: 0;
							bottom: 0;
							left: 0;
							width: 42%;
							border-radius: inherit;
							background: currentColor;
							animation: attachment-transfer-indeterminate 1.2s ease-in-out infinite;
						}
					}

					&.attachment-transfer--overlay {
						position: absolute;
						left: 0;
						right: 0;
						bottom: 0;
						z-index: 2;
						padding: 10px 12px 12px;
						background: linear-gradient(180deg, rgba(var(--im-black-rgb), 0) 0%, rgba(var(--im-black-rgb), 0.56) 100%);
						color: rgba(var(--im-white-rgb), 0.98);
					}

					&.attachment-transfer--overlay-dark {
						background: linear-gradient(180deg, rgba(var(--im-black-rgb), 0.06) 0%, rgba(var(--im-black-rgb), 0.7) 100%);
					}

						&.attachment-transfer--inline {
							width: 100%;
							padding-top: 2px;
							color: var(--im-text-color-light);

							.attachment-transfer__bar {
								background: rgba(var(--im-black-rgb), 0.08);

							span {
								background: var(--im-color-primary);
							}
						}
					}
				}

				.message-image {
					display: inline-flex;
					width: fit-content;
					flex: 0 0 auto;
					max-width: 100%;
					border-radius: 10px;
					overflow: hidden;
					cursor: default;
					background: var(--im-control-bg);
					border: 1px solid var(--im-control-border);
					box-shadow: 0 10px 24px -24px rgba(var(--im-black-rgb), 0.3);
					transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
					position: relative;

					&.message-image--interactive {
						cursor: pointer;
					}

					&:hover {
						border-color: rgba(var(--im-primary-rgb), 0.18);
						box-shadow: 0 14px 28px -24px rgba(var(--im-black-rgb), 0.34);
						background: rgba(var(--im-primary-rgb), 0.02);
					}

					.image-container {
						position: relative;
						display: inline-flex;
						width: fit-content;
						flex: 0 0 auto;
						max-width: 100%;
						overflow: hidden;
						border-radius: 10px;
						line-height: 0;
						background: linear-gradient(180deg, rgba(var(--im-black-rgb), 0.04), rgba(var(--im-black-rgb), 0.08));

						.image-placeholder {
							position: absolute;
							inset: 0;
							overflow: hidden;
							display: flex;
							align-items: center;
							justify-content: center;
							background: linear-gradient(180deg, rgba(var(--im-black-rgb), 0.04), rgba(var(--im-black-rgb), 0.08));
							z-index: 2;
						}

						.image-placeholder--error {
							background: linear-gradient(180deg, rgba(var(--im-danger-rgb), 0.06), rgba(var(--im-danger-rgb), 0.1));
						}

						.image-placeholder__content {
							position: relative;
							z-index: 1;
							display: inline-flex;
							flex-direction: column;
							align-items: center;
							justify-content: center;
							gap: 8px;
							padding: 12px;
							text-align: center;
							color: var(--im-text-color-light);
							line-height: 1.4;

							i {
								display: inline-flex;
								align-items: center;
								justify-content: center;
								width: 36px;
								height: 36px;
								border-radius: 999px;
								background: rgba(var(--im-white-rgb), 0.72);
								color: var(--im-text-color-secondary);
								font-size: 18px;
								box-shadow: 0 8px 18px -16px rgba(var(--im-black-rgb), 0.4);
							}

							span {
								display: block;
								max-width: 140px;
								font-size: 12px;
								font-weight: 500;
								letter-spacing: 0.2px;
							}
						}

						.image-placeholder--error .image-placeholder__content {
							color: var(--im-color-danger);

							i {
								background: rgba(var(--im-white-rgb), 0.78);
								color: var(--im-color-danger);
							}
						}

						.image-placeholder__shimmer {
							position: absolute;
							top: 0;
							bottom: 0;
							left: -42%;
							width: 42%;
							background: linear-gradient(90deg, rgba(var(--im-white-rgb), 0), rgba(var(--im-white-rgb), 0.52), rgba(var(--im-white-rgb), 0));
							animation: attachment-image-shimmer 1.4s ease-in-out infinite;
						}

						:deep(img) {
							display: block;
							width: 100%;
							height: auto;
							max-width: 100%;
						}

						.send-image {
							display: block;
							width: 100%;
							max-width: 100%;
							position: relative;
							z-index: 1;
							object-fit: cover;
							opacity: 0;
							transition: opacity 0.2s ease;
						}

						.send-image--ready {
							opacity: 1;
						}

						.image-overlay {
							position: absolute;
							top: 0;
							left: 0;
							right: 0;
							bottom: 0;
							background: linear-gradient(180deg, rgba(var(--im-black-rgb), 0.06) 0%, rgba(var(--im-black-rgb), 0.32) 100%);
							display: flex;
							align-items: center;
							justify-content: center;
							opacity: 0;
							transition: opacity 0.2s ease;

							i {
								display: inline-flex;
								align-items: center;
								justify-content: center;
								width: 38px;
								height: 38px;
								border-radius: 999px;
								background: rgba(var(--im-white-rgb), 0.18);
								backdrop-filter: blur(6px);
								color: var(--im-background);
								font-size: 18px;
							}
						}

						&:hover {
							.image-overlay {
								opacity: 1;
							}
						}
					}
				}

				.message-sticker {
					display: inline-flex;
					width: fit-content;
					flex: 0 0 auto;
					max-width: 100%;
					overflow: hidden;
					position: relative;

					img {
						display: block;
						max-width: 100%;
					}
				}

				.message-video {
					display: inline-block;
					width: fit-content;
					flex: 0 0 auto;
					max-width: min(100%, 360px);
					border-radius: 12px;
					overflow: hidden;
					background: var(--im-control-bg);
					border: 1px solid var(--im-control-border);
					box-shadow: 0 10px 24px -24px rgba(var(--im-black-rgb), 0.3);
					line-height: 0;
					cursor: pointer;

					.video-shell {
						position: relative;
						width: min(100%, 360px);
						min-width: 180px;
						min-height: 132px;
						display: flex;
						align-items: center;
						justify-content: center;
						background: linear-gradient(135deg, rgba(var(--im-black-rgb), 0.74), rgba(var(--im-primary-rgb), 0.16));

						.video-poster {
							display: block;
							width: 100%;
							height: auto;
							max-height: 360px;
							object-fit: contain;
						}

						.video-poster--empty {
							width: 100%;
							aspect-ratio: 16 / 9;
							background: linear-gradient(135deg, rgba(var(--im-black-rgb), 0.7), rgba(var(--im-primary-rgb), 0.14));
						}

						.video-mask {
							position: absolute;
							inset: 0;
							display: flex;
							align-items: center;
							justify-content: center;
							background: linear-gradient(180deg, rgba(var(--im-black-rgb), 0.02), rgba(var(--im-black-rgb), 0.18));
							color: #fff;
							font-size: 24px;

							.video-mask__inner {
								display: inline-flex;
								align-items: center;
								justify-content: center;
								gap: 8px;
							}

							.video-mask__inner--stacked {
								flex-direction: column;
								gap: 10px;
							}

							i {
								display: inline-flex;
								align-items: center;
								justify-content: center;
								width: 46px;
								height: 46px;
								border-radius: 999px;
								background: rgba(var(--im-black-rgb), 0.28);
								border: 1px solid rgba(var(--im-white-rgb), 0.18);
								backdrop-filter: blur(8px);
							}

							span {
								font-size: 12px;
								line-height: 1.4;
								font-weight: 500;
								letter-spacing: 0.2px;
								color: rgba(var(--im-white-rgb), 0.92);
							}
						}

						.video-mask--loading {
							font-size: 13px;
							background: rgba(var(--im-black-rgb), 0.3);
						}

						.video-mask--placeholder {
							background: linear-gradient(180deg, rgba(var(--im-black-rgb), 0.12), rgba(var(--im-black-rgb), 0.32));
						}

						.video-mask--error {
							background: linear-gradient(180deg, rgba(var(--im-danger-rgb), 0.08), rgba(var(--im-black-rgb), 0.5));

							i {
								background: rgba(var(--im-white-rgb), 0.16);
								border-color: rgba(var(--im-white-rgb), 0.22);
							}

							span {
								color: rgba(var(--im-white-rgb), 0.96);
							}
						}
					}

					.send-video {
						display: block;
						width: auto;
						min-width: 180px;
						max-width: min(360px, 100%);
						height: auto;
						max-height: 360px;
						object-fit: contain;
						border-radius: 8px;
					}
				}

				.message-file {
					display: flex;
					max-width: 100%;
					min-width: 0;
					flex-wrap: nowrap;
					flex-direction: row;
					align-items: center;
					cursor: pointer;
					margin-bottom: 2px;
					background: transparent;

					.file-box {
						display: flex;
						flex-direction: column;
						align-items: stretch;
						gap: 10px;
						min-height: 68px;
						border-radius: 12px;
						padding: 12px 14px;
						border: 1px solid var(--im-control-border);
						transition: border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;
						background: var(--im-control-bg);
						box-shadow: 0 10px 24px -24px rgba(var(--im-black-rgb), 0.3);
						width: min(100%, 344px);
						max-width: 100%;
						min-width: 0;
						box-sizing: border-box;
						cursor: default;

						&.file-box--interactive {
							cursor: pointer;

							&:hover {
								border-color: rgba(var(--im-primary-rgb), 0.18);
								background: rgba(var(--im-primary-rgb), 0.04);
								box-shadow: 0 14px 28px -24px rgba(var(--im-black-rgb), 0.34);

								.file-name {
									color: var(--im-color-primary);
								}
							}
						}

						&.file-box--uploading {
							border-color: rgba(var(--im-primary-rgb), 0.2);
							background: rgba(var(--im-primary-rgb), 0.03);
						}

						&.file-box--failed {
							border-color: rgba(var(--im-danger-rgb), 0.2);
							background: rgba(var(--im-danger-rgb), 0.03);

							.attachment-transfer--inline {
								color: var(--im-color-danger);
							}
						}

						.file-main {
							display: flex;
							align-items: center;
							gap: 12px;
							min-width: 0;

							.file-info {
								flex: 1;
								min-width: 0;
								height: 100%;
								text-align: left;
								font-size: 14px;

								.file-name {
									display: -webkit-box;
									width: 100%;
									max-width: 100%;
									font-size: 14px;
									line-height: 20px;
									font-weight: 600;
									margin-bottom: 4px;
									max-height: 40px;
									overflow: hidden;
									-webkit-box-orient: vertical;
									-webkit-line-clamp: 2;
									word-break: break-all;
									color: var(--im-text-color-primary);
									transition: color 0.2s ease;
								}

								.file-meta {
									display: flex;
									align-items: center;
									gap: 8px;
									flex-wrap: wrap;
								}

								.file-type-tag,
								.file-size {
									display: inline-flex;
									align-items: center;
									height: 20px;
									padding: 0 8px;
									border-radius: 999px;
									font-size: 12px;
									font-weight: 500;
									letter-spacing: 0.2px;
								}

								.file-size {
									background: rgba(var(--im-black-rgb), 0.04);
									color: var(--im-text-color-light);
								}

								.file-type-tag {
									background: rgba(var(--im-black-rgb), 0.06);
									color: var(--im-text-color-secondary);
								}

								.file-type-tag--primary {
									background: rgba(var(--im-primary-rgb), 0.1);
									color: var(--im-color-primary);
								}

								.file-type-tag--success {
									background: rgba(var(--im-success-rgb, 103, 194, 58), 0.12);
									color: var(--im-color-success, #67c23a);
								}

								.file-type-tag--warning {
									background: rgba(var(--im-warning-rgb, 230, 162, 60), 0.12);
									color: var(--im-color-warning, #e6a23c);
								}

								.file-type-tag--danger {
									background: rgba(var(--im-danger-rgb), 0.1);
									color: var(--im-color-danger);
								}

								.file-type-tag--teal {
									background: rgba(20, 184, 166, 0.12);
									color: #0f766e;
								}
							}

							.file-icon {
								display: inline-flex;
								align-items: center;
								justify-content: center;
								flex-shrink: 0;
								width: 44px;
								height: 44px;
								border-radius: 12px;
								background: rgba(var(--im-black-rgb), 0.05);
								border: 1px solid rgba(var(--im-black-rgb), 0.06);

								.file-icon__label {
									display: inline-flex;
									align-items: center;
									justify-content: center;
									min-width: 0;
									font-size: 12px;
									line-height: 1;
									font-weight: 700;
									letter-spacing: 0.6px;
									color: var(--im-text-color-secondary);
								}

								&.file-icon--primary {
									background: rgba(var(--im-primary-rgb), 0.08);
									border-color: rgba(var(--im-primary-rgb), 0.12);
								}

								&.file-icon--primary .file-icon__label {
									color: var(--im-color-primary);
								}

								&.file-icon--success {
									background: rgba(var(--im-success-rgb, 103, 194, 58), 0.1);
									border-color: rgba(var(--im-success-rgb, 103, 194, 58), 0.14);
								}

								&.file-icon--success .file-icon__label {
									color: var(--im-color-success, #67c23a);
								}

								&.file-icon--warning {
									background: rgba(var(--im-warning-rgb, 230, 162, 60), 0.1);
									border-color: rgba(var(--im-warning-rgb, 230, 162, 60), 0.14);
								}

								&.file-icon--warning .file-icon__label {
									color: var(--im-color-warning, #e6a23c);
								}

								&.file-icon--danger {
									background: rgba(var(--im-danger-rgb), 0.08);
									border-color: rgba(var(--im-danger-rgb), 0.12);
								}

								&.file-icon--danger .file-icon__label {
									color: var(--im-color-danger);
								}

								&.file-icon--teal {
									background: rgba(20, 184, 166, 0.1);
									border-color: rgba(20, 184, 166, 0.14);
								}

								&.file-icon--teal .file-icon__label {
									color: #0f766e;
								}
							}
						}
					}
				}

				.message-voice {
					display: inline-flex;
					max-width: 100%;

					.voice-chip {
						display: inline-flex;
						align-items: center;
						gap: 10px;
						min-width: 132px;
						max-width: 100%;
						min-height: 44px;
						padding: 0 14px;
						border: 1px solid var(--im-control-border);
						border-radius: 999px;
						background: var(--im-control-bg);
						color: var(--im-text-color);
						cursor: pointer;
						box-shadow: none;
						transition: border-color 0.2s ease, background-color 0.2s ease, color 0.2s ease;

						&:hover {
							border-color: rgba(var(--im-primary-rgb), 0.18);
							background: rgba(var(--im-primary-rgb), 0.04);
						}
					}

					.voice-chip__icon {
						display: inline-flex;
						align-items: center;
						justify-content: center;
						width: 28px;
						height: 28px;
						border-radius: 999px;
						background: rgba(var(--im-black-rgb), 0.06);
						color: var(--im-color-primary);
						flex-shrink: 0;

						i {
							font-size: 14px;
						}
					}

					.voice-chip__wave {
						display: inline-flex;
						align-items: flex-end;
						gap: 3px;
						height: 16px;
						flex: 1;
						min-width: 0;
					}

					.voice-chip__bar {
						width: 3px;
						border-radius: 999px;
						background: currentColor;
						opacity: 0.42;
						transform-origin: center bottom;
					}

					.voice-chip__bar:nth-child(1) {
						height: 7px;
					}

					.voice-chip__bar:nth-child(2) {
						height: 12px;
					}

					.voice-chip__bar:nth-child(3) {
						height: 9px;
					}

					.voice-chip__bar:nth-child(4) {
						height: 14px;
					}

					.voice-chip__duration {
						flex-shrink: 0;
						font-size: 12px;
						font-weight: 600;
						color: var(--im-text-color-light);
					}

					.voice-chip--playing {
						border-color: rgba(var(--im-primary-rgb), 0.22);
						background: rgba(var(--im-primary-rgb), 0.05);

						.voice-chip__bar {
							opacity: 0.88;
							animation: voice-chip-wave 1.05s ease-in-out infinite;
						}

						.voice-chip__bar:nth-child(2) {
							animation-delay: 0.08s;
						}

						.voice-chip__bar:nth-child(3) {
							animation-delay: 0.16s;
						}

						.voice-chip__bar:nth-child(4) {
							animation-delay: 0.24s;
						}
					}

					.voice-chip--loading {
						cursor: progress;

						.voice-chip__wave {
							opacity: 0.38;
						}
					}
				}

				.chat-action {
					display: flex;
					align-items: center;

					.iconfont {
						cursor: pointer;
						font-size: 22px;
						padding-right: 8px;
					}
				}

				.quote-message {
					display: block;
					margin-top: 6px;
					cursor: pointer;
					max-width: min(100%, 680px);
				}

				.message-send-state {
					margin-top: 6px;
					display: inline-flex;
					align-items: center;
					max-width: min(100%, 680px);

					.message-send-state-icon {
						display: inline-flex;
						align-items: center;
						justify-content: center;
						width: 20px;
						height: 20px;
						padding: 0;
						border: none;
						border-radius: 999px;
						background: transparent;
						font-size: 12px;
						line-height: 1;
					}

					.message-send-state-icon--sending {
						background: rgba(var(--im-black-rgb), 0.045);
						color: var(--im-text-color-light);
					}

					.message-send-state-icon--failed {
						background: rgba(var(--im-danger-rgb), 0.08);
						color: var(--im-color-danger);
						cursor: pointer;
						transition: background-color 0.2s ease, transform 0.2s ease;

						&:hover {
							background: rgba(var(--im-danger-rgb), 0.14);
							transform: translateY(-1px);
						}
					}
				}

				.message-status {
					margin-top: 6px;
					display: inline-flex;
					align-items: center;
					max-width: min(100%, 680px);

					.message-status-icon {
						display: inline-flex;
						align-items: center;
						justify-content: center;
						gap: 4px;
						height: 20px;
						min-width: 20px;
						padding: 0 6px;
						border-radius: 999px;
						font-size: 12px;
						white-space: nowrap;

						&.message-status-icon--readed {
							background: rgba(var(--im-success-rgb), 0.09);
							color: var(--im-color-success);
						}

						&.message-status-icon--unread {
							background: rgba(var(--im-black-rgb), 0.045);
							color: var(--im-text-color-light);
						}

						&.message-status-icon--pending {
							background: rgba(var(--im-black-rgb), 0.045);
							color: var(--im-text-color-light);
						}

						.message-status-text {
							font-size: 11px;
							line-height: 1;
						}
					}
				}

				.chat-receipt {
					margin-top: 6px;
					display: inline-flex;
					align-items: center;
					gap: 4px;
					width: fit-content;
					min-height: 20px;
					padding: 0 8px;
					border-radius: 999px;
					background: rgba(var(--im-primary-rgb), 0.06);
					font-size: 11px;
					font-weight: 500;
					cursor: pointer;
					color: var(--im-text-color-light);
					max-width: min(100%, 680px);
					transition: background-color 0.2s ease, color 0.2s ease;

					&:hover {
						background: rgba(var(--im-primary-rgb), 0.1);
						color: var(--im-color-primary);
					}

					.chat-receipt__pie {
						position: relative;
						width: 12px;
						height: 12px;
						flex: 0 0 12px;
						border-radius: 50%;
						box-shadow: inset 0 0 0 1px rgba(var(--im-black-rgb), 0.08);

						&::after {
							content: '';
							position: absolute;
							left: 3px;
							top: 3px;
							width: 6px;
							height: 6px;
							border-radius: 50%;
							background: var(--im-background);
						}

						&.chat-receipt__pie--readed {
							box-shadow: inset 0 0 0 1px rgba(var(--im-success-rgb), 0.18);
						}
					}
				}

				:deep(.chat-at-user) {
					padding: 2px 5px;
					border-radius: 3px;
					cursor: pointer;
					color: var(--im-color-primary);
					background: rgba(var(--im-primary-rgb), 0.12);
				}
			}
		}

		&.message-mine {
			text-align: right;
			padding-left: 0;
			padding-right: 56px;

			.avatar {
				left: auto;
				right: 0;
			}

			.content {
				align-items: flex-end;
				text-align: right;

				.top {
					flex-direction: row-reverse;
				}

				.bottom {
					align-items: flex-end;
					padding-left: 32px;
					padding-right: 0;

					.message-main-row {
						justify-content: flex-end;
						align-self: flex-end;
					}

					.message-content-wrapper {
						flex-direction: row-reverse;
					}

					.message-pin-indicator {
						left: -4px;
						right: auto;
					}

					.message-text {
						background: var(--bubble-sent-bg);
						color: var(--bubble-sent-text);
						border: none;
						border-radius: 16px 8px 16px 16px;
					}

					:deep(.chat-at-user) {
						color: rgba(var(--im-white-rgb), 0.9);
						cursor: pointer;
					}

					.chat-action {
						flex-direction: row-reverse;

						.iconfont {
							transform: rotateY(180deg);
						}
					}

					.quote-message,
					.message-send-state,
					.message-status,
					.chat-receipt {
						align-self: flex-end;
					}
				}
			}
		}
	}
}

@keyframes attachment-transfer-indeterminate {
	0% {
		transform: translateX(-120%);
	}

	100% {
		transform: translateX(240%);
	}
}

@keyframes attachment-image-shimmer {
	0% {
		transform: translateX(0);
	}

	100% {
		transform: translateX(320%);
	}
}

@keyframes voice-chip-wave {
	0%,
	100% {
		transform: scaleY(0.55);
	}

	50% {
		transform: scaleY(1);
	}
}

@media (max-width: 640px) {
	.chat-message-item {
		padding: 4px 10px;

		.message-normal {
			padding-left: 52px;

			.bottom {
				padding-right: 24px;

				.message-text {
					padding: 7px 11px;
					line-height: 22px;
				}
			}

			&.message-mine {
				padding-right: 52px;

				.bottom {
					padding-left: 24px;
				}
			}
		}
	}
}
</style>
